import { Box, Grid, GridItem, Heading, Text, useToast } from "@chakra-ui/react";
import LegendlessDonutChart from "../../../../../../../../components/LegendlessDonutChart/LegendlessDonutChart";
import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  useGetNumberOfFinalisedSupplierQuestionnairesByType
} from "../../../../../../../../hooks/query/reports";
import { showErrorToast } from "../../../../../../../../utils";
import { CenterLabelLayer } from "./CenterLabelLayer";
import { roundByLargestRemainderMethod } from "../../../../../../../Dashboard/helpers";
import { Card } from "../../../../../../../../components/Card";
import { SUPPLIER_QUESTIONNAIRE_CATEGORIES } from "../../../../../../../SupplierQuestionnaire/helpers";

function FinalisedQuestionnairesByType({ categoryId }) {
  const { t } = useTranslation("translation");
  const toast = useToast();

  const {
    data: questionnairesByTypeData
  } = useGetNumberOfFinalisedSupplierQuestionnairesByType(categoryId, {
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0
  });

  const mapData = (typesData, year) => {
    const mappedData = [];

    const supplierQuestionnairesTypes = [
      {
        id: "Product",
        title: "Product",
        key: SUPPLIER_QUESTIONNAIRE_CATEGORIES.PRODUCT,
        color: "hsl(21,77%,46%)"
      },
      {
        id: "Location",
        title: "Location",
        key: SUPPLIER_QUESTIONNAIRE_CATEGORIES.LOCATION,
        color: "hsl(199, 72%, 30%)"
      },
      {
        id: "Revenue",
        title: "Revenue",
        key: SUPPLIER_QUESTIONNAIRE_CATEGORIES.REVENUE,
        color: "hsl(128, 62%, 26%)"
      }
    ];

    const totalValueForYear = typesData.reduce((n, { count }) => n + count, 0);

    typesData.forEach(el => {
      const foundStatus = supplierQuestionnairesTypes.find(typeObj => typeObj.key === el.type) ?? {};
      mappedData.push({
        ...foundStatus,
        value: el.count,
        percentageValue: (el.count / totalValueForYear) * 100,
        year: year
      });
    });

    const roundedValues = roundByLargestRemainderMethod(mappedData.map(e => e.percentageValue));
    mappedData.forEach((el, index) => {
      el.percentageValue = roundedValues[index];
    });

    return mappedData;
  };

  const getTooltip = (tooltipObj) => {
    const { datum: { data } } = tooltipObj;
    const formattedPercentageValue = data.percentageValue && data.percentageValue > 0 ?
      data.percentageValue.toLocaleString("en-GB", { maximumFractionDigits: 1 }) : "";

    const elIdx = Object.keys(questionnairesByTypeData).findIndex(item => item.toString() === data.year);
    // Since we display donut charts in 4 columns per row, this is how we evaluate if the current chart
    // will be displayed in the first column
    const isInFirstColumn = elIdx !== -1 && ((elIdx + 1) % 4 === 1);

    return (
      <Box position="relative">
        <Card
          position="absolute"
          minWidth={"350px"}
          padding={3}
          left={isInFirstColumn ? "100%" : null}
          right={!isInFirstColumn ? "100%" : null}
        >
          <Text fontSize="sm" textAlign="start">
            <b>{data.title}</b> for {data.year} - <b>{data.value} Supplier{data.value > 1 ? "s" : ""} ({formattedPercentageValue}%)</b>
          </Text>
        </Card>
      </Box>);
  };

  const getLabel = (el) => {
    return <>
      <tspan x="0" dy="-0.6em">{el.data.title}</tspan>
      <tspan x="0" dy="1.4em">{el.data.value}</tspan>
    </>
  };

  return (
    <Box>
      <Heading size="md" marginBottom={4}>
        {t("reportsPage.suppliersData.supplierEngagement.finalisedQuestionnairesByType")}
      </Heading>
      {questionnairesByTypeData && typeof questionnairesByTypeData !== "undefined" &&
      Object.keys(questionnairesByTypeData).length > 0 ?
        <Grid templateColumns="repeat(4, 1fr)" gap={2}>
          {
            Object.keys(questionnairesByTypeData).map(yearKey => {
              return <GridItem
                key={`type-chart-container-${yearKey}`}
                height={"300px"}
                textAlign="center"
                padding={4}
              >
                <LegendlessDonutChart
                  data={mapData(questionnairesByTypeData[yearKey], yearKey)}
                  getTooltip={getTooltip}
                  getLabel={getLabel}
                  getLabelsTextColor={() => "white"}
                  CustomLayerComponent={CenterLabelLayer(yearKey)}
                />
              </GridItem>;
            })
          }
        </Grid>
        : <Text fontSize="lg" fontWeight="bold">
          {t("noDataAvailable")}
        </Text>
      }
    </Box>
  );
}

export default FinalisedQuestionnairesByType;